import * as R from 'ramda';

import { QUICK_LINKS_DATASOURCES } from '../../../main/screens/ScreenQuickLinks/constants';
import {
  CALCULATED_ECPA,
  CALCULATED_ECPA_IN_LOCAL_CURRENCY,
  CALCULATED_ECPC,
  CALCULATED_ECPC_IN_LOCAL_CURRENCY,
  CALCULATED_ECPI,
  CALCULATED_ECPI_IN_LOCAL_CURRENCY,
  CALCULATED_ECPM,
  CALCULATED_ECPM_IN_LOCAL_CURRENCY,
  CALCULATED_ROAS,
  CALCULATED_ROI,
} from './calculations';
import {
  COST,
  COST_ACTIONS,
  COST_CLICKS,
  COST_IMPRESSIONS,
  COST_INSTALLS,
  EO_COMMERCE_EVENT,
  EO_INSTALL,
  SK_ADNETWORK_DATASOURCE,
  ZEIT_CLICK,
  ZEIT_IMPRESSION,
} from './datasources';

/* -------------------
 Dimension Constants
-------------------- */
const USER_DATA_PLATFORM = 'user_data_platform';
const NOT_USER_DATA_PLATFORM = `!${USER_DATA_PLATFORM}`;
const FROM_DESKTOP = 'from_desktop';
const PLUS_CURRENT_FEATURE = 'last_attributed_touch_data_plus_current_feature';
const NOT_PLUS_CURRENT_FEATURE = `!${PLUS_CURRENT_FEATURE}`;
const PLUS_VIA_FEATURES = 'last_attributed_touch_data_plus_via_features';
const TILDE_FEATURE = 'last_attributed_touch_data_tilde_feature';

// Web Platforms
const IOS_WEB = 'IOS_WEB';
const ANDROID_WEB = 'ANDROID_WEB';
const DESKTOP_WEB = 'DESKTOP_WEB';
const TV_WEB = 'TV_WEB';
// APP Platforms
const IOS_APP = 'IOS_APP';
const ANDROID_APP = 'ANDROID_APP';
const IOS_TV = 'IOS_TV';
const ANDROID_TV = 'ANDROID_TV';
const DESKTOP_APP = 'DESKTOP_APP';
const TV_APP = 'TV_APP';
// Other Platforms
const ROBOT = 'ROBOT';
const OTHER = 'OTHER';

// Feature types
const EMAIL = 'EMAIL';
const MOBILE_DEEPVIEWS = 'MOBILE_DEEPVIEWS';
const DESKTOP_DEEPVIEWS = 'DESKTOP_DEEPVIEWS';
const JOURNEYS = 'journeys';

/* -----------------
 Platform Filters
-------------------- */
const ALL_PLATFORMS_FILTER = { [NOT_USER_DATA_PLATFORM]: [ROBOT, null] };
const APP_PLATFORMS_FILTER = {
  [USER_DATA_PLATFORM]: [IOS_APP, ANDROID_APP, IOS_TV, ANDROID_TV, DESKTOP_APP, TV_APP],
};
const WEB_PLATFORMS_FILTER = {
  [USER_DATA_PLATFORM]: [IOS_WEB, ANDROID_WEB, DESKTOP_WEB, TV_WEB],
};
const MOBILE_WEB_PLATFORMS_FILTER = {
  [USER_DATA_PLATFORM]: [IOS_WEB, ANDROID_WEB],
};
const NOT_ROBOT_FILTER = { [NOT_USER_DATA_PLATFORM]: [ROBOT] };

/* ------------------------------------------------------------------------------
 Source Queries
 This is a potentially poor name for this, but the idea is that different datasources
 require a different set of default filters. (i.e. clicks should not include
 an app platform and should only come from web). Instead of adding this specific logic
 info every query that gets created, we have define these standard set of query adjustments
 here and then merge them into each query on the server.
-------------------------------------------------------------------------------- */

/**
 * NOTE: Don't use postive filters for click or impressions. Only negative (!user_data_platform vs user_data_platform)
 * In Zeit (SANS ad network service), some fields are always null (i.e. Google Adwords does not provide a platform)
 * Adding a positive filter for user_data_platform will ensure no results are ever returned for Google Adwords
 * Negative filters allow for null values to still be returned from Zeit
 */
const CLICK_SOURCE_QUERY = {
  filters: {
    [NOT_USER_DATA_PLATFORM]: [ROBOT, OTHER],
    [NOT_PLUS_CURRENT_FEATURE]: [DESKTOP_DEEPVIEWS],
  },
};

const getSourceQueries = () => {
  const COST_QUERIES = {
    [CALCULATED_ECPA]: {
      calculation: {
        fn: '/',
        fields: [
          { data_source: COST_ACTIONS },
          {
            data_source: EO_COMMERCE_EVENT,
            name: 'PURCHASE',
          },
        ],
      },
    },
    [CALCULATED_ECPA_IN_LOCAL_CURRENCY]: {
      calculation: {
        fn: '/',
        fields: [
          {
            data_source: COST_ACTIONS,
            metric: 'cost_in_app_local_currency',
          },
          {
            data_source: EO_COMMERCE_EVENT,
            name: 'PURCHASE',
          },
        ],
      },
    },
    [CALCULATED_ECPC]: {
      calculation: {
        fn: '/',
        fields: [{ data_source: COST_CLICKS }, { data_source: ZEIT_CLICK }],
      },
    },
    [CALCULATED_ECPC_IN_LOCAL_CURRENCY]: {
      calculation: {
        fn: '/',
        fields: [
          {
            data_source: COST_CLICKS,
            metric: 'cost_in_app_local_currency',
          },
          { data_source: ZEIT_CLICK },
        ],
      },
    },
    [CALCULATED_ECPI]: {
      calculation: {
        fn: '/',
        fields: [{ data_source: COST_INSTALLS }, { data_source: EO_INSTALL }],
      },
    },
    [CALCULATED_ECPI_IN_LOCAL_CURRENCY]: {
      calculation: {
        fn: '/',
        fields: [
          {
            data_source: COST_INSTALLS,
            metric: 'cost_in_app_local_currency',
          },
          { data_source: EO_INSTALL },
        ],
      },
    },
    [CALCULATED_ECPM]: {
      calculation: {
        fn: '/',
        fields: [
          { data_source: COST_IMPRESSIONS },
          {
            fn: '/',
            fields: [{ data_source: ZEIT_IMPRESSION }, { constant: 1000 }],
          },
        ],
      },
    },
    [CALCULATED_ECPM_IN_LOCAL_CURRENCY]: {
      calculation: {
        fn: '/',
        fields: [
          {
            data_source: COST_IMPRESSIONS,
            metric: 'cost_in_app_local_currency',
          },
          {
            fn: '/',
            fields: [{ data_source: ZEIT_IMPRESSION }, { constant: 1000 }],
          },
        ],
      },
    },
  };

  return {
    ...COST_QUERIES,
    [CALCULATED_ROI]: {
      calculation: {
        fn: '/',
        metric: 'percentage',
        fields: [
          {
            fn: '-',
            fields: [
              {
                data_source: EO_COMMERCE_EVENT,
                name: 'PURCHASE',
                metric: 'revenue',
              },
              { data_source: COST },
            ],
          },
          { data_source: COST },
        ],
      },
    },
    [CALCULATED_ROAS]: {
      calculation: {
        fn: '/',
        metric: 'percentage',
        fields: [
          {
            data_source: EO_COMMERCE_EVENT,
            name: 'PURCHASE',
            metric: 'revenue',
          },
          { data_source: COST },
        ],
      },
    },
    // Click and Impression currently can't filter out null
    // This will cause no SAN data to return
    eo_click: CLICK_SOURCE_QUERY,
    eo_impression: { filters: NOT_ROBOT_FILTER },

    // Topics that come from all platforms
    eo_user_lifecycle_event: { filters: ALL_PLATFORMS_FILTER },
    eo_content_event: { filters: ALL_PLATFORMS_FILTER },
    eo_custom_event: { filters: ALL_PLATFORMS_FILTER },

    // Topics that only come from app platforms including TV
    eo_install: { filters: APP_PLATFORMS_FILTER },
    eo_open: { filters: APP_PLATFORMS_FILTER },
    eo_reinstall: { filters: APP_PLATFORMS_FILTER },

    // Topics that only come from web
    eo_pageview: { filters: WEB_PLATFORMS_FILTER },
    eo_branch_cta_view: { filters: WEB_PLATFORMS_FILTER },
    eo_web_session_start: { filters: WEB_PLATFORMS_FILTER },
    eo_sms_sent: { filters: WEB_PLATFORMS_FILTER },

    eo_dismissal: { filters: WEB_PLATFORMS_FILTER },

    [EO_COMMERCE_EVENT]: {
      filters: ALL_PLATFORMS_FILTER,
      aggregations: ['total_count', 'revenue', 'revenue_in_local_currency'],
    },
    [SK_ADNETWORK_DATASOURCE]: {
      aggregations: ['total_count'],
    },
  };
};

export const SOURCE_QUERIES = getSourceQueries();

/**
 Certain charts have specific required filters that need to be added.
 Do that here.
 */
export const JOURNEYS_SUMMARY_TILE_SOURCE_QUERIES = {
  eo_pageview: {
    filters: MOBILE_WEB_PLATFORMS_FILTER,
  },
  eo_branch_cta_view: {
    filters: {
      ...MOBILE_WEB_PLATFORMS_FILTER,
      [TILDE_FEATURE]: [JOURNEYS],
    },
  },
  eo_click: {
    filters: {
      ...MOBILE_WEB_PLATFORMS_FILTER,
      [TILDE_FEATURE]: [JOURNEYS],
    },
  },
  eo_open: {
    filters: {
      [TILDE_FEATURE]: [JOURNEYS],
    },
  },
  eo_dismissal: {
    filters: {
      ...MOBILE_WEB_PLATFORMS_FILTER,
      [TILDE_FEATURE]: [JOURNEYS],
    },
  },
  eo_install: {
    filters: {
      [TILDE_FEATURE]: [JOURNEYS],
    },
  },
};

export const DEEPVIEWS_SUMMARY_SECTION_SOURCE_QUERIES = {
  // CURRENT FEATURE
  eo_branch_cta_view: {
    filters: {
      ...MOBILE_WEB_PLATFORMS_FILTER,
      [PLUS_CURRENT_FEATURE]: [MOBILE_DEEPVIEWS],
    },
  },
  eo_click: {
    filters: {
      ...MOBILE_WEB_PLATFORMS_FILTER,
      [PLUS_CURRENT_FEATURE]: [MOBILE_DEEPVIEWS],
    },
  },

  // VIA FEATURES
  eo_install: {
    filters: {
      ...APP_PLATFORMS_FILTER,
      [PLUS_VIA_FEATURES]: [MOBILE_DEEPVIEWS],
    },
  },
  eo_open: {
    filters: {
      ...APP_PLATFORMS_FILTER,
      [PLUS_VIA_FEATURES]: [MOBILE_DEEPVIEWS],
    },
  },
  eo_reinstall: {
    filters: {
      ...APP_PLATFORMS_FILTER,
      [PLUS_VIA_FEATURES]: [MOBILE_DEEPVIEWS],
    },
  },
};

export const CLICK_FLOW_SOURCE_QUERIES = {
  eo_click: {
    ...CLICK_SOURCE_QUERY,
    dimensions: [USER_DATA_PLATFORM, FROM_DESKTOP],
  },
  eo_sms_sent: {
    filters: { [USER_DATA_PLATFORM]: [DESKTOP_WEB] },
    dimensions: [USER_DATA_PLATFORM],
  },
  eo_install: {
    filters: APP_PLATFORMS_FILTER,
    dimensions: [USER_DATA_PLATFORM, FROM_DESKTOP],
  },
  eo_open: {
    filters: APP_PLATFORMS_FILTER,
    dimensions: [USER_DATA_PLATFORM, FROM_DESKTOP],
  },
  eo_reinstall: {
    filters: APP_PLATFORMS_FILTER,
    dimensions: [USER_DATA_PLATFORM, FROM_DESKTOP],
  },
};

const CALCULATED_EVENT_CALCULATIONS = {
  GROSS_PROFIT: {
    fn: '-',
    fields: [
      {
        data_source: EO_COMMERCE_EVENT,
        name: 'PURCHASE',
        metric: 'revenue',
      },
      { data_source: COST },
    ],
  },
};

export const ADS_TIMESERIES_SOURCE_QUERIES = R.merge(
  SOURCE_QUERIES,
  Object.keys(CALCULATED_EVENT_CALCULATIONS).reduce((acc, calculatedEvent) => {
    return R.assoc(`calculated_${calculatedEvent}`, { calculation: CALCULATED_EVENT_CALCULATIONS[calculatedEvent] }, acc);
  }, {})
);

export const QUICK_LINKS_SOURCE_QUERIES = R.pick(QUICK_LINKS_DATASOURCES, SOURCE_QUERIES);

/**
 Helper functions for adding filters and dimensions to standard source queries.
 This gives us the ability to be flexible while still using the standard set of source queries
 */
function addFilterToSourceQuery(filterName, filterValues, excludeDatasources) {
  return R.mapObjIndexed((val, key) => {
    if (excludeDatasources && excludeDatasources.includes(key)) {
      return val;
    }
    const filterLens = R.lensPath(['filters', filterName]);
    /**
     Need remove the opposite filter value
     i.e. if the standard SOURCE_QUERY filter is { '!user_data_platform': 'IOS_APP' } and for a specific
     chart, we want to filter only on IOS_APP, we should first remove '!user_data_platform': 'IOS_APP'
     then add user_data_platform: 'IOS_APP' If we don't remove this, there is a chance for a very confusing conflict
    */
    const oppositeFilterName = filterName.includes('!') ? filterName.substring(1) : `!${filterName}`;
    val = R.dissocPath(['filters', oppositeFilterName], val);

    return R.set(filterLens, filterValues, val);
  }, SOURCE_QUERIES);
}

/**
 for email eo_click we want to use +current_feature as our filter.
 for all other email data sources, we want to use +via_feature as our filter
   Ex: User clicks a link in an email >> clicks on a Journey banner >> opens app.
   * We want the eo_open event included in Email analytics but not the journey click.

    * eo_click for Journey click:
        +current_feature='JOURNEYS',
        +via_features will include 'EMAIL'
    * this click will be EXCLUDED bc eo_click only filters on +current_feature

    * eo_open event:
        +current_feature will not be set,
        +via_features will include 'EMAIL'
    * this click will be INCLUDED bc all other events filters on +via_feature
  */
export const EMAIL_SOURCE_QUERIES = R.mergeDeepRight(addFilterToSourceQuery(PLUS_VIA_FEATURES, [EMAIL], ['eo_click']), {
  eo_click: {
    filters: {
      [PLUS_CURRENT_FEATURE]: [EMAIL],
    },
  },
});
